import { Box, Button, HStack, Text, Tooltip, VStack } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  ExpenseOrderByColumnEnum,
  ExpenseStatusEnum,
  type ExpensesForPaymentMatchQuery,
  ExpensesWhereColumn,
  ExpenseTypeEnum,
  PaymentStatusEnum,
  SqlOperator,
} from "@src/__generated__/graphql";
import type { DetailTableCustomProps } from "@src/components/modules/bank/match-payment-modal";
import { BankStore } from "@src/components/modules/bank/stores";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  Link,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { appStore } from "@src/stores/AppStore";
import { getPaymentStatusFilterOptions } from "@src/utils/components/filters/constants";
import { Filter } from "@src/utils/components/filters/models";
import { contractorOptionsQuery } from "@src/utils/components/filters/paginatedOptions";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { currency, dateTime, formatDate } from "@src/utils/formatters";
import { camelCase, lowerCase, map, upperFirst } from "lodash";
import type { MatchPaymentTableItem } from "./table-state";

type ExpenseMatchRow = NonNullable<
  ExpensesForPaymentMatchQuery["expenses"]
>["data"][0] &
  MatchPaymentTableItem;

export function createExpenseFiltersArgs(): Filter<ExpensesWhereColumn>[] {
  return [
    new Filter({
      column: ExpensesWhereColumn.Type,
      operator: SqlOperator.In,
      title: t`Type`,
      options: map(ExpenseTypeEnum, (type) => {
        if (type === ExpenseTypeEnum.Card) {
          return {
            label: t`Card payments`,
            value: camelCase(type),
          };
        }

        return {
          label: upperFirst(lowerCase(type)),
          value: camelCase(type),
        };
      }),
    }),
    new Filter({
      column: ExpensesWhereColumn.PaymentStatus,
      operator: SqlOperator.In,
      title: t`Payment status`,
      options: getPaymentStatusFilterOptions(),
      value: [PaymentStatusEnum.NotPaid, PaymentStatusEnum.PartiallyPaid],
    }),
    new Filter({
      column: ExpensesWhereColumn.Status,
      hidden: true,
      operator: SqlOperator.Eq,
      value: [ExpenseStatusEnum.Approved],
      options: [],
    }),
    new Filter({
      column: ExpensesWhereColumn.ContractorId,
      isPaginated: true,
      optionsQuery: contractorOptionsQuery,
      operator: SqlOperator.In,
      title: t`Contractor`,
      options: [],
    }),
    new Filter({
      column: ExpensesWhereColumn.BrandId,
      operator: SqlOperator.In,
      title: t`Client/Brand`,
      tagTitle: t`Brand`,
      options: [],
      sectioned: true,
      hasSelectAllOption: true,
    }),
    new Filter({
      column: ExpensesWhereColumn.WorkspaceCompanyId,
      operator: SqlOperator.In,
      title: t`Legal entity`,
      options: [],
    }),
    new Filter({
      column: ExpensesWhereColumn.WorkspaceBankAccountId,
      operator: SqlOperator.In,
      title: t`Agency bank account`,
      options: [],
    }),
    new Filter({
      column: ExpensesWhereColumn.DueDate,
      operator: SqlOperator.Between,
      dateRange: true,
      title: t`Due date`,
      options: [],
    }),
    new Filter({
      column: ExpensesWhereColumn.DateOfSupply,
      operator: SqlOperator.Between,
      dateRange: true,
      title: t`Date of supply`,
      options: [],
      value: [],
    }),
    new Filter({
      column: ExpensesWhereColumn.AmountFrom,
      operator: SqlOperator.Gte,
      title: t`Amount from`,
      options: [],
      mutateToUrl: BankStore.mutateAmountFilter,
      hidden: true,
    }),
    new Filter({
      column: ExpensesWhereColumn.AmountTo,
      operator: SqlOperator.Lte,
      title: t`Amount to`,
      options: [],
      mutateToUrl: BankStore.mutateAmountFilter,
      hidden: true,
    }),
  ];
}

export function createExpenseColumnsArgs(
  customProps: DetailTableCustomProps,
): BodyColumnDef<ExpenseMatchRow, ExpenseOrderByColumnEnum>[] {
  const columns: BodyColumnDef<ExpenseMatchRow, ExpenseOrderByColumnEnum>[] = [
    {
      id: "id",
      meta: { label: t`ID` },
      header: () => t`ID`,
      orderBy: ExpenseOrderByColumnEnum.Id,
      cell: ({ row }) => (
        <Link
          href={{
            pathname: "/expenses/edit",
            query: { id: row.original.id },
          }}
        >
          {row.original.id}
        </Link>
      ),
    },
    {
      id: "expense-no",
      meta: { label: t`Expense no` },
      header: () => t`Expense no`,
      orderBy: ExpenseOrderByColumnEnum.ExpenseNoRaw,
      cell: ({ row }) => (
        <Link
          href={{
            pathname: "/expenses/edit",
            query: { id: row.original.id },
          }}
          maxW="120px"
          isTruncated
        >
          {row.original.expense_no}
        </Link>
      ),
    },
    {
      id: "type",
      meta: { label: t`Type` },
      header: () => t`Type`,
      orderBy: ExpenseOrderByColumnEnum.Type,
      cell: ({ row }) => row.original.type.toUpperCase(),
    },
    {
      id: "contractor-legal_name",
      meta: { label: t`Contractor` },
      header: () => t`Contractor`,
      orderBy: ExpenseOrderByColumnEnum.Contractor,
      cell: ({ row }) => (
        <WrapComponent
          if={!row.original.has_master_agreement}
          with={(children) => (
            <Tooltip
              label={t`This is a regular invoice and the contractor does not have any active Master Agreement`}
              placement="top"
            >
              <HStack>
                {children}
                <Icon name="alert-circle" color="red" />
              </HStack>
            </Tooltip>
          )}
        >
          {row.original.contractor && (
            <Link
              href={{
                pathname: "/contractors/detail",
                query: { id: row.original.contractor.id },
              }}
              isTruncated
              maxW="100px"
            >
              {row.original.contractor.legal_name}
            </Link>
          )}
        </WrapComponent>
      ),
    },
    {
      id: "projects",
      meta: { label: t`Projects` },
      header: () => t`Projects`,
      cell: ({ row }) => (
        <VStack alignItems="start" maxW="140px" spacing="2">
          {row.original.projects.map((project) => (
            <Tooltip
              key={project.id}
              hasArrow
              label={`${project.code} – ${project.title.replace(" ", "-")}`}
              placement="top-start"
            >
              <Box>
                <Link
                  href={{
                    pathname: "/projects/detail",
                    query: { id: project.id },
                  }}
                >
                  <div>{project.code}</div>
                  <Text maxW="120px" isTruncated>
                    {project.title}
                  </Text>
                </Link>
              </Box>
            </Tooltip>
          ))}
        </VStack>
      ),
    },
    {
      id: "workspace-company",
      meta: { label: t`Workspace company` },
      header: () => t`Workspace company`,
      cell: ({ row }) => (
        <Text maxW="100px" isTruncated>
          {row.original.workspace_company_id &&
            appStore.workspaceStore.getCompany(
              row.original.workspace_company_id,
            )?.name}
        </Text>
      ),
    },
    {
      id: "variable-symbol",
      meta: { label: t`Var. symbol` },
      header: () => t`Var. symbol`,
      orderBy: ExpenseOrderByColumnEnum.VariableSymbol,
      cell: ({ row }) => (
        <Text isTruncated>{row.original.variable_symbol}</Text>
      ),
    },
    {
      id: "iban",
      meta: { label: t`IBAN` },
      header: () => t`IBAN`,
      orderBy: ExpenseOrderByColumnEnum.Iban,
      cell: ({ row }) =>
        row.original.iban && (
          <Tooltip label={row.original.iban} placement="top">
            <Text>{row.original.iban.slice(0, 5)}...</Text>
          </Tooltip>
        ),
    },
    {
      id: "amount-with-vat",
      meta: { label: t`Amount (with VAT)` },
      header: () => t`Amount (with VAT)`,
      orderBy: ExpenseOrderByColumnEnum.Amount,
      cell: ({ row }) => (
        <Tooltip
          label={
            <Text>
              <Text>
                <Trans>Amount:</Trans>&nbsp;
                {currency.formatByCurrency(
                  row.original.stats.total_amount_without_vat_in_currency,
                  row.original.currency,
                )}
              </Text>
              <Text>
                <Trans>Amount (VAT):</Trans>&nbsp;
                {currency.formatByCurrency(
                  row.original.stats.total_amount_with_vat_in_currency,
                  row.original.currency,
                )}
              </Text>
            </Text>
          }
          placement="top"
          shouldWrapChildren
        >
          {currency.formatByCurrency(
            row.original.stats.total_amount_with_vat_in_currency,
            row.original.currency,
          )}
        </Tooltip>
      ),
    },
    {
      id: "date-of-supply",
      meta: { label: t`Date of supply` },
      header: () => t`Date of supply`,
      orderBy: ExpenseOrderByColumnEnum.DateOfSupply,
      cell: ({ row }) =>
        row.original.date_of_supply
          ? dateTime.format(new Date(row.original.date_of_supply))
          : "",
    },
    {
      id: "due-date",
      meta: { label: t`Due date` },
      header: () => t`Due date`,
      orderBy: ExpenseOrderByColumnEnum.DueDate,
      cell: ({ row }) =>
        row.original.date_of_supply
          ? dateTime.format(new Date(row.original.date_of_supply))
          : "",
    },
    {
      id: "paid",
      meta: { label: t`Paid` },
      header: () => t`Paid`,
      cell: ({ row }) => {
        const paid = row.original.accountMoves?.reduce(
          (acc, curr) => acc + curr.amount,
          0,
        );

        if (!paid) return null;

        return currency.formatByCurrency(paid, row.original.currency);
      },
    },
    {
      id: "paidAt",
      meta: { label: t`Paid at` },
      header: () => t`Paid at`,
      orderBy: ExpenseOrderByColumnEnum.PaidAt,
      cell: ({ row }) =>
        row.original.paid_at
          ? formatDate(new Date(row.original.paid_at))
          : null,
    },
    {
      id: ACTIONS_COLUMN_ID,
      meta: { label: t`Actions` },
      header: () => t`Actions`,
      size: 150,
      cell: ({ row }) => {
        const selectedRows = customProps.selectedRows();
        const isSelected = selectedRows.includes(row.original.id);

        return (
          <Button
            colorScheme={isSelected ? "red" : undefined}
            onClick={() => {
              customProps.onMatchClick(
                isSelected
                  ? selectedRows.filter((id) => id !== row.original.id)
                  : selectedRows.concat(row.original.id),
              );
            }}
            size="sm"
            variant="ghost"
          >
            {isSelected ? <Trans>Remove match</Trans> : <Trans>Match</Trans>}
          </Button>
        );
      },
    },
  ];

  return appStore.workspaceStore.hasMultipleCompanies()
    ? columns
    : columns.filter((column) => column.id !== "workspace-company");
}
